<template>
  <a-breadcrumb style="margin: 16px 0">
    <a-breadcrumb-item>SIG&Committer</a-breadcrumb-item>
    <a-breadcrumb-item>Committer 分析</a-breadcrumb-item>
  </a-breadcrumb>
  <a-spin :spinning="loading">
    <div class="page">
      <section class="section">
        <div class="section-title">
          <span class="section-title-icon" :style="{ color: '#C27AFF' }">
            <ClusterOutlined />
          </span>
          SIG 数量
        </div>
        <div class="sig-grid">
          <div v-for="card in sigCards" :key="card.type" class="sig-card" :class="card.themeClass">
            <div class="sig-card-header">
              <span class="sig-card-title">{{ card.title }}</span>
            </div>
            <div class="sig-count-row">
              <div class="sig-count-box">
                <div class="sig-count-label">华为</div>
                <div class="sig-count-value">{{ card.huaweiCount }}</div>
              </div>
              <div class="sig-count-box">
                <div class="sig-count-label">非华为</div>
                <div class="sig-count-value">{{ card.nonHuaweiCount }}</div>
              </div>
              <div class="sig-count-box sig-count-total">
                <div class="sig-count-label">合计</div>
                <div class="sig-count-value">{{ card.totalCount }}</div>
              </div>
            </div>
            <div class="sig-count-action">
              <a-button type="link" size="small" class="sig-count-btn" @click="openSigModal(card.type)">
                查看 {{ card.title }} SIG 列表
              </a-button>
            </div>
          </div>
        </div>
        <a-modal v-model:open="sigModalOpen" title="SIG 列表" :footer="null" width="1000px">
          <div class="sig-modal-header">{{ modalTitle }}</div>
          <div class="sig-modal-body">
            <div class="sig-modal-column">
              <div class="sig-modal-title">华为 SIG（{{ modalHuaweiCount }}）</div>
              <div class="sig-modal-list">
                <div v-for="name in modalHuaweiList" :key="name" class="sig-modal-item">{{ name }}</div>
              </div>
              <div v-if="!modalHuaweiList.length" class="sig-empty">暂无</div>
            </div>
            <div class="sig-modal-column">
              <div class="sig-modal-title">非华为 SIG（{{ modalNonHuaweiCount }}）</div>
              <div class="sig-modal-list">
                <div v-for="name in modalNonHuaweiList" :key="name" class="sig-modal-item">{{ name }}</div>
              </div>
              <div v-if="!modalNonHuaweiList.length" class="sig-empty">暂无</div>
            </div>
          </div>
        </a-modal>
      </section>


      <section class="section">
        <div class="section-header">
          <div class="section-title">
            <span class="section-title-icon" :style="{ color: '#7C3AED' }">
              <PieChartOutlined />
            </span>
            Committer 仓数量分布
          </div>
        </div>
        <div class="distribution-row">
          <div v-for="item in distributionStats" :key="item.label" class="distribution-card"
            :style="{ borderColor: item.color }">
            <div class="distribution-label">{{ item.label }}</div>
            <div class="distribution-value">{{ item.value }} <span class="stat-unit">个 Committer</span></div>
          </div>
        </div>
        <div class="chart-card">
          <my-bar-chart :categories="barCategories" :values="barValues" :colors="barColors" :bar-width="100" />
        </div>
      </section>
    </div>
  </a-spin>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import axios from 'axios';
import { notification } from 'ant-design-vue';
import MyBarChart from '@/measurement-platform/components/MyBarChart.vue';
import { ClusterOutlined, PieChartOutlined } from '@ant-design/icons-vue';

const sigData = ref({
  HUAWEI: { hasCount: 0, noCount: 0, hasList: [], noList: [] },
  NON_HUAWEI: { hasCount: 0, noCount: 0, hasList: [], noList: [] }
});

const distributionStats = ref([]);

const barCategories = ['<3个仓', '3-5个仓', '>5个仓'];
const barValues = ref([0, 0, 0]);
const barColors = ['#3B82F6', '#22C55E', '#F59E0B'];
const loading = ref(false);

const parseSigList = (value) => {
  if (!value) return [];
  return String(value)
    .split(',')
    .map((name) => name.trim())
    .filter((name) => name.length > 0);
};

const updateSigData = (rows) => {
  const huawei = rows.find((item) => item.type === 'HUAWEI') || {};
  const nonHuawei = rows.find((item) => item.type === 'NON_HUAWEI') || {};
  sigData.value = {
    HUAWEI: {
      hasCount: Number(huawei.has_repo_count) || 0,
      noCount: Number(huawei.no_repo_count) || 0,
      hasList: parseSigList(huawei.has_repo_sig_list),
      noList: parseSigList(huawei.no_repo_sig_list)
    },
    NON_HUAWEI: {
      hasCount: Number(nonHuawei.has_repo_count) || 0,
      noCount: Number(nonHuawei.no_repo_count) || 0,
      hasList: parseSigList(nonHuawei.has_repo_sig_list),
      noList: parseSigList(nonHuawei.no_repo_sig_list)
    }
  };
};

const updateDistribution = (data) => {
  const lt3 = Number(data?.['committerNum < 3']) || 0;
  const between = Number(data?.['committerNum >= 3 && committerNum <= 5']) || 0;
  const gt5 = Number(data?.['committerNum > 5']) || 0;
  distributionStats.value = [
    { label: '<3个仓', value: lt3, color: '#3B82F6' },
    { label: '3-5个仓', value: between, color: '#22C55E' },
    { label: '>5个仓', value: gt5, color: '#F59E0B' }
  ];
  barValues.value = [lt3, between, gt5];
};

const fetchSigRepoStatus = async () => {
  const res = await axios.get('/contributorController/getSigRepoStatus');
  if (res.data?.code !== 200) {
    throw new Error(res.data?.desc || '请求失败');
  }
  updateSigData(res.data?.data || []);
};

const fetchCommitterDistribution = async () => {
  const res = await axios.get('/contributorController/getAllRepoCommitters');
  if (res.data?.code !== 200) {
    throw new Error(res.data?.desc || '请求失败');
  }
  updateDistribution(res.data?.data || {});
};

const sigModalOpen = ref(false);
const sigModalType = ref('HAS');

const sigCards = computed(() => {
  const huawei = sigData.value.HUAWEI;
  const nonHuawei = sigData.value.NON_HUAWEI;
  return [
    {
      type: 'HAS',
      title: '有仓',
      themeClass: 'sig-card-has',
      huaweiCount: huawei.hasCount,
      nonHuaweiCount: nonHuawei.hasCount,
      totalCount: huawei.hasCount + nonHuawei.hasCount,
      huaweiList: huawei.hasList,
      nonHuaweiList: nonHuawei.hasList
    },
    {
      type: 'NO',
      title: '无仓',
      themeClass: 'sig-card-no',
      huaweiCount: huawei.noCount,
      nonHuaweiCount: nonHuawei.noCount,
      totalCount: huawei.noCount + nonHuawei.noCount,
      huaweiList: huawei.noList,
      nonHuaweiList: nonHuawei.noList
    }
  ];
});

const openSigModal = (type) => {
  sigModalType.value = type;
  sigModalOpen.value = true;
};

const isHasRepo = computed(() => sigModalType.value === 'HAS');
const modalTitle = computed(() => (isHasRepo.value ? '有仓' : '无仓'));
const modalHuaweiCount = computed(() => isHasRepo.value
  ? sigData.value.HUAWEI.hasCount
  : sigData.value.HUAWEI.noCount);
const modalNonHuaweiCount = computed(() => isHasRepo.value
  ? sigData.value.NON_HUAWEI.hasCount
  : sigData.value.NON_HUAWEI.noCount);
const modalHuaweiList = computed(() => isHasRepo.value
  ? sigData.value.HUAWEI.hasList
  : sigData.value.HUAWEI.noList);
const modalNonHuaweiList = computed(() => isHasRepo.value
  ? sigData.value.NON_HUAWEI.hasList
  : sigData.value.NON_HUAWEI.noList);

onMounted(async () => {
  loading.value = true;
  try {
    await Promise.all([fetchSigRepoStatus(), fetchCommitterDistribution()]);
  } catch (error) {
    notification.error({
      message: '数据加载失败',
      description: error?.message || error
    });
  } finally {
    loading.value = false;
  }
});
</script>


<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 16px;
}

.section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 18px 22px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #0f172a;
}

.section-title-icon {
  display: inline-flex;
  align-items: center;
  font-size: 18px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.section-filter {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-filter-label {
  color: #4b5563;
  font-weight: 500;
}

.panel-row {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
}

.panel {
  flex: 1;
  min-width: 280px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0 16px 16px 16px;
  background: #f8fafc;
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.12);
}

.panel-header {
  color: #fff;
  font-weight: 600;
  padding: 10px 14px;
  border-radius: 8px;
  text-align: center;
  margin: 12px 0 14px 0;
  letter-spacing: 0.5px;
}

.panel-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stat-card {
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px 14px;
  background: #fff;
  min-height: 86px;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
}

.total-card {
  font-weight: 600;
  min-height: 44px;
  padding: 8px 12px;
  box-shadow: none;
}

.stat-name {
  font-size: 15px;
  color: #374151;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-dot {
  font-weight: 700;
}

.stat-card-center {
  text-align: center;
}

.stat-card-center .stat-name {
  justify-content: flex-start;
  width: 100%;
}

.stat-card-center .stat-value {
  justify-content: flex-end;
  text-align: right;
  width: 100%;
}

.total-card-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.total-card-row .stat-name {
  margin-bottom: 0;
  font-size: 14px;
}

.total-card-row .stat-value {
  font-size: 20px;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
}

.stat-unit {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
}

.distribution-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.distribution-card {
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.distribution-label {
  font-weight: 600;
  margin-bottom: 10px;
  color: #1f2937;
}

.distribution-value {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
}

.chart-card {
  margin-top: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 16px 8px 16px;
  background: #fff;
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.12);
}

.sig-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}

.sig-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.sig-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.sig-card-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}


.sig-count-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}

.sig-count-box {
  background: #ffffff;
  border-radius: 12px;
  padding: 12px;
  border: 1px solid #e2e8f0;
}

.sig-count-label {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
}

.sig-count-value {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}

.sig-count-total {
  background: #f8fafc;
  border-color: #e2e8f0;
}

.sig-count-btn {
  padding: 0;
  height: auto;
  margin-top: 6px;
}

.sig-count-action {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}

.sig-card-has {
  border-color: rgba(5, 223, 114, 0.32);
  background: rgba(5, 223, 114, 0.05);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
}

.sig-card-has .sig-card-title {
  color: #0f766e;
}

.sig-card-has .sig-count-value {
  color: #059669;
}

.sig-card-has .sig-count-btn {
  color: #059669;
}

.sig-card-no {
  border-color: rgba(194, 122, 255, 0.32);
  background: rgba(194, 122, 255, 0.05);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
}

.sig-card-no .sig-card-title {
  color: #6b21a8;
}

.sig-card-no .sig-count-value {
  color: #7c3aed;
}

.sig-card-no .sig-count-btn {
  color: #7c3aed;
}

.sig-modal-header {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 12px;
}

.sig-modal-body {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}

.sig-modal-column {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  max-height: 360px;
  overflow: auto;
}

.sig-modal-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 10px;
}

.sig-modal-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px;
}

.sig-modal-item {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  color: #000;
}
</style>